import type { PatientState } from '../create-user'
import type { Patient } from '../queries'
import type { ChangeEvent, Dispatch, ReactNode, SetStateAction } from 'react'
import type { ZodError } from 'zod'

import Select from 'react-select'
// @ts-expect-error // it's okay
import stateMapping from 'shared_src/dictionaries/usStateCodes'

import * as Grid from '../../shared/grid'

interface CreatePatientFormProps {
  state: Partial<PatientState | undefined>
  setState: Dispatch<SetStateAction<Partial<PatientState> | undefined>>
  formError: ZodError<any> | null
}

const genders = [{
  value: 'Male',
  label: 'Male'
}, {
  value: 'Female',
  label: 'Female'
},
{
  value: 'Other',
  label: 'Other'
}]

const yesOrNot = [{
  value: true,
  label: 'Yes'
}, {
  value: false,
  label: 'No'
}]

const states = stateMapping.map((s: { label: string, code: string }) => ({
  label: s.label,
  value: s.code
})) as Array<{ label: string, value: string }>

export function EditPatientForm ({ state, setState, formError }: CreatePatientFormProps): JSX.Element {
  function handleChange (key: keyof Patient) {
    return (value: string | boolean) => {
      // @ts-expect-error // it's okay
      setState((prev) => {
        if (key === 'email' && typeof value === 'string') {
          value = value.replaceAll(' ', '')
        }

        return {
          ...(prev ?? {}),
          data: {
            // @ts-expect-error // it's okay
            ...(prev.data ?? {}),
            [key]: value
          }
        }
      })
    }
  }

  function handleRawChange (key: keyof Patient) {
    return (e: ChangeEvent<HTMLTextAreaElement>) => {
      // @ts-expect-error // it's okay
      setState((prev) => ({
        ...(prev ?? {}),
        data: {
          // @ts-expect-error // it's okay
          ...(prev.data ?? {}),
          [key]: e.target.value
        }
      }))
    }
  }

  const errors = formError?.flatten()

  return (
    <div className='px-8 flex-1'>
      <Row>
        <Column>
          <Header>
            Personal Info
          </Header>

          <Grid.Input
            errorMessage={errors?.fieldErrors?.firstName?.[0]}
            value={state?.data?.firstName ?? ''}
            onChange={handleChange('firstName')}
            placeholder='First Name'
          />
          <Grid.Input
            errorMessage={errors?.fieldErrors?.lastName?.[0]}
            value={state?.data?.lastName ?? ''}
            onChange={handleChange('lastName')}
            placeholder='Last Name'
          />
          <Grid.SelectWrapper
            label='Gender'
            errorMessage={errors?.fieldErrors?.gender?.[0]}
          >
            <Select
              options={genders}
              value={genders.find((g) => state?.data?.gender?.toLowerCase() === g.value.toLowerCase()) ?? undefined}
              onChange={(value) => value && handleChange('gender')(value.value)}
              placeholder='Gender'
            />
          </Grid.SelectWrapper>
          <Grid.Input
            type='date'
            errorMessage={errors?.fieldErrors?.dob?.[0]}
            value={state?.data?.dob ?? ''}
            onChange={handleChange('dob')}
            placeholder='Date of Birth'
          />
          <Grid.Input
            errorMessage={errors?.fieldErrors?.email?.[0]}
            value={state?.data?.email ?? ''}
            onChange={handleChange('email')}
            placeholder='Email'
          />
          <Grid.Input
            errorMessage={errors?.fieldErrors?.phone?.[0]}
            value={state?.data?.phone ?? ''}
            onChange={handleChange('phone')}
            placeholder='Phone'
          />
        </Column>

        <Column>
          <Header>
            Address
          </Header>

          <Column>
            <Grid.Input
              errorMessage={errors?.fieldErrors?.addressLine1?.[0]}
              value={state?.data?.addressLine1 ?? ''}
              onChange={handleChange('addressLine1')}
              placeholder='Address 1'
            />
            <Grid.Input
              errorMessage={errors?.fieldErrors?.addressLine2?.[0]}
              value={state?.data?.addressLine2 ?? ''}
              onChange={handleChange('addressLine2')}
              placeholder='Address 2'
            />
            <Grid.Input
              errorMessage={errors?.fieldErrors?.cityName?.[0]}
              value={state?.data?.cityName ?? ''}
              onChange={handleChange('cityName')}
              placeholder='City Name'
            />
            <Grid.Input
              errorMessage={errors?.fieldErrors?.zipCode?.[0]}
              value={state?.data?.zipCode ?? ''}
              onChange={handleChange('zipCode')}
              placeholder='Zip Code'
            />
            <Grid.SelectWrapper
              label='State'
              errorMessage={errors?.fieldErrors?.stateCode?.[0]}
            >
              <Select
                options={states}
                value={states.find((v) => state?.data?.stateCode === v.value) ?? undefined}
                onChange={(value) => value && handleChange('stateCode')(value.value)}
                placeholder='Select State'
              />
            </Grid.SelectWrapper>

            <Grid.Input
              errorMessage={errors?.fieldErrors?.countryCode?.[0]}
              value={state?.data?.countryCode ?? ''}
              onChange={handleChange('countryCode')}
              placeholder='Country Code'
            />

            <Grid.SelectWrapper
              label='Is Billing Address'
              errorMessage={errors?.fieldErrors?.isBillingAddress?.[0]}
            >
              <Select
                options={yesOrNot}
                value={yesOrNot.find((v) => state?.data?.isBillingAddress === v.value) ?? undefined}
                onChange={(value) => value && handleChange('isBillingAddress')(value.value)}
                placeholder='Is Billing Address'
              />
            </Grid.SelectWrapper>
          </Column>
        </Column>
      </Row>

      <hr className='bg-natural-200 mt-12 mb-3' />

      <Row>
        <Column>
          <Header>
            Patient Health Info
          </Header>
          <Grid.Input
            value={state?.data?.currentCondition ?? ''}
            onChange={handleChange('currentCondition')}
            placeholder='Current Condition'
          />

          <Grid.Input
            value={state?.data?.allergies ?? ''}
            onChange={handleChange('allergies')}
            placeholder='Allergies'
          />

          <Grid.Input
            value={state?.data?.medicalCondition ?? ''}
            onChange={handleChange('medicalCondition')}
            placeholder='Medical Condition'
          />
        </Column>

        <div className='flex flex-1 gap-10 flex-col flex-wrap justify-end'>
          <Header>&nbsp;</Header>
          <Grid.Textarea
            className='h-full'
            value={state?.data?.patientNotes ?? ''}
            onChange={handleRawChange('patientNotes')}
            placeholder='Patient Notes'
          />
        </div>
      </Row>
      <div className='text-gray-500 mt-10'>
        COMPASSION Member ID:
        <span className='text-zinc-800'> {state?.data?.patientId}</span>
      </div>
      <div className='text-gray-500 mt-2'>UBACARE ID: <span className='text-zinc-800'>{state?.data?.ubaPatientId}</span>
      </div>
    </div>
  )
}

function Row ({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className='flex gap-16 w-full flex-row flex-wrap py-2'>
      {children}
    </div>
  )
}

function Column ({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className='flex flex-1 gap-10 h-fit flex-col flex-wrap justify-end'>
      {children}
    </div>
  )
}

function Header ({ children }: { children: ReactNode }): JSX.Element {
  return (
    <h3 style={{ fontSize: '1.25rem', fontWeight: '500', padding: '1.5rem 0 0 0', margin: 0 }}>
      {children}
    </h3>
  )
}
